import { Badge, Box, Typography } from '@mui/material'
import { TypographyVariant } from '@mui/material/styles/createTypography'
import { AppAvatar } from 'components/core/avatar'
import { AvatarSize } from 'components/core/avatar/AvatarSizes'
import { C1DisplayNameSkeleton, DisplayNameDetails } from 'components/core/display-name-details'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { capitalizeFirstLetter } from 'components/no-ui/string-formatting'
import { resourceEntitlementStyle } from 'components/pages/entitlement/resource/styles'
import { ResourceTypeAvatar } from 'components/pages/entitlement/resource-type/icons'
import { AppResourceTypeTraits } from 'lib/constants/app'
import { routes, Route } from 'lib/routes'
import { AppResource, AppResourceType, AppResourceServiceGetRequest } from 'pbts/c1/api/app/v1/app_resource'
import { FC, useMemo } from 'react'
import { getAppResourceApi } from 'redux_toolkit/app-resources'

export type ResourceTypeDisplayNameProps = {
    resource: AppResource
    resourceType?: AppResourceType
    appName?: string
    avatarSize?: AvatarSize
    titleVariant?: TypographyVariant
    readOnly?: boolean
    includeDetails?: boolean
    includeType?: boolean
    includeDescription?: boolean
    forceResourceDetail?: boolean
}

export type EntitlementResourceDisplayNameProps = {
    resource: AppResource
    resourceType?: AppResourceType
    appName?: string
    displayName: string
    avatarSize?: AvatarSize
    titleVariant?: TypographyVariant
    readOnly?: boolean
}

export const urlFromAppResource = (resource: AppResource, resourceType?: AppResourceType, forceResourceDetail?: boolean): Route => {
    if (forceResourceDetail) {
        return routes.applications.resource(resource.appId, resource.appResourceTypeId, resource.id)
    }
    const isGroup = resourceType?.traitIds?.includes(AppResourceTypeTraits.group) ?? false
    if (isGroup) {
        return routes.applications.resourceGroup(resource.appId, resource.appResourceTypeId, resource.id)
    }
    return routes.applications.resource(resource.appId, resource.appResourceTypeId, resource.id)
}
export const ResourceDisplayName = ({
    resource,
    resourceType,
    appName,
    avatarSize = 'md',
    titleVariant = 'body1',
    readOnly = false,
    includeDetails = false,
    includeType = false,
    includeDescription = false,
    forceResourceDetail = false,
}: ResourceTypeDisplayNameProps): React.ReactElement => {
    const avatar = (
        <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={<AppAvatar appId={resource.appId || resourceType?.appId} size="xs" appName={appName} />}
        >
            <ResourceTypeAvatar resourceTypeName={resourceType?.displayName || ''} size={avatarSize} />
        </Badge>
    )
    const detailsPageURL = readOnly
        ? undefined
        : urlFromAppResource(resource, resourceType, forceResourceDetail)

    const details = useMemo(() => {
        const data: string[] = []
        if (includeDetails && appName) {
            data.push(appName)
        }
        const resourceTypeName = capitalizeFirstLetter(resourceType?.displayName)
        if (includeType && resourceTypeName) {
            data.push(resourceTypeName)
        }
        return data.length > 0 ? data.join(' • ') : undefined
    }, [includeDetails, appName, includeType, resourceType])

    const description = useMemo(
        () => (resource.customDescription !== '' ? resource.customDescription : resource.description),
        [resource.description, resource.customDescription],
    )

    return (
        <Box>
            <DisplayNameDetails
                avatar={avatar}
                displayName={resource.displayName}
                link={detailsPageURL}
                displayNameVariant={titleVariant}
                details={details}
            />
            {includeDescription && description && (
                <Typography sx={resourceEntitlementStyle.description}>
                    {description}
                </Typography>
            )}
        </Box>
    )
}

export const EntitlementResourceDisplayName = ({
    resource,
    resourceType,
    appName,
    displayName,
    avatarSize = 'sm',
    titleVariant = 'body1',
    readOnly = false,
}: EntitlementResourceDisplayNameProps): React.ReactElement => {
    const avatar = (
        <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={<AppAvatar size="xs" appName={appName} />}
        >
            <ResourceTypeAvatar resourceTypeName={resourceType?.displayName || ''} size={avatarSize} />
        </Badge>
    )
    const isGroup = resourceType?.traitIds?.includes(AppResourceTypeTraits.group) ?? false
    const detailsPageURL = readOnly
        ? undefined
        : isGroup
            ? routes.applications.resourceGroup(resource.appId, resource.appResourceTypeId, resource.id)
            : routes.applications.resource(resource.appId, resource.appResourceTypeId, resource.id)
    return (
        <Box sx={resourceEntitlementStyle.box}>
            <DisplayNameDetails
                avatar={avatar}
                displayName={displayName}
                link={detailsPageURL}
                displayNameVariant={titleVariant}
            />
        </Box>
    )
}

export const ResourceDisplayNameWithFetch: FC<{
    appEntitlement: { appId: string; appResourceTypeId: string; appResourceId: string }
    readOnly?: boolean
}> = ({ appEntitlement, readOnly = false }) => {
    const { data, isLoading } = getAppResourceApi.useGet(
        AppResourceServiceGetRequest.create({
            appId: appEntitlement.appId,
            appResourceTypeId: appEntitlement.appResourceTypeId,
            id: appEntitlement.appResourceId,
            expandMask: { paths: ['*'] },
        }),
    )

    if (isLoading) return <C1DisplayNameSkeleton />

    if (!data) return <C1EmptyIndicator />

    return <ResourceDisplayName resource={data} resourceType={data.appResourceType} readOnly={readOnly} avatarSize="sm" />
}
